import styles from "./main.module.scss";
import { ReactComponent as Clock } from "../../assets/clock.svg";
import { ReactComponent as Calendar } from "../../assets/calendar.svg";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAccreditationByReadMoreId,
  getAdmissionReqByReadMoreId,
  getAdmissionReqDetailsByAdmissionReqId,
  getAllApplicationFeeByProgramId,
  getAllCurriculumByReadMoreId,
  getAllScholarshipByReadMoreId,
  getAllTestimonialByReadMoreId,
  getAllTuitionYearByTuitionId,
  getReadMoreCareerProspectByReadMoreId,
  getReadMoreCareerProspectItemByCareerProspectId,
  getReadMoreCourseOverviewByReadMoreId,
  getReadMoreProgrammeByReadMoreId,
  getTuitionByReadMoreId,
} from "./request";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { Spin } from "antd";

const CourseDetails = () => {
  const [active, setActive] = useState(1);
  const [activeYearTab, setActiveYearTab] = useState(1);
  const [activeCurriculumTab, setActiveCurriculumTab] = useState(1);
  const [prospectId, setProspectId] = useState("");
  const [admissionReqId, setAdmissionReqId] = useState("");
  const [tuitionId, setTuitionId] = useState("");

  const { id, programTypeId } = useParams();
  const navigate = useNavigate();
  const readMoreId = id ?? "";

  const queries = useQueries({
    queries: [
      {
        queryKey: ["get-read-more-programmes"],
        queryFn: () => getReadMoreProgrammeByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-read-more-course-overview"],
        queryFn: () => getReadMoreCourseOverviewByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-read-more-career-prospect"],
        queryFn: () => getReadMoreCareerProspectByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-read-more-career-prospect-id"],
        queryFn: () =>
          getReadMoreCareerProspectItemByCareerProspectId(prospectId),
        enabled: !!Number(prospectId),
      },
      {
        queryKey: ["get-all-scholarship"],
        queryFn: () => getAllScholarshipByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-all-testimonial"],
        queryFn: () => getAllTestimonialByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-accreditation"],
        queryFn: () => getAccreditationByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-tuition"],
        queryFn: () => getTuitionByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-application-fee"],
        queryFn: () => getAllApplicationFeeByProgramId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-all-curriculum"],
        queryFn: () => getAllCurriculumByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
    ],
  });

  const nextQueries = useQueries({
    queries: [
      {
        queryKey: ["get-admission-req"],
        queryFn: () => getAdmissionReqByReadMoreId(readMoreId),
        enabled: !!readMoreId,
      },
      {
        queryKey: ["get-admission-req-details"],
        queryFn: () => getAdmissionReqDetailsByAdmissionReqId(admissionReqId),
        enabled: !!Number(admissionReqId),
      },
      {
        queryKey: ["get-tuition-year"],
        queryFn: () => getAllTuitionYearByTuitionId(tuitionId),
        enabled: !!Number(tuitionId),
      },
    ],
  });

  const readMoreProgrammesQueries = queries[0];
  const readMoreCourseOverviewQueries = queries[1];
  const readMoreCareerProspectQueries = queries[2];
  const careerProspectItemQueries = queries[3];
  const scholarshipQueries = queries[4];
  const testimonialQueries = queries[5];
  const accreditationQueries = queries[6];
  const tuitionQueries = queries[7];
  const applicationFeeQueries = queries[8];
  const curriculumQueries = queries[9];

  const admissionReqQueries = nextQueries[0];
  const admissionReqDetailsQueries = nextQueries[1];
  const tuitionYearQueries = nextQueries[2];

  const programmeData =
    readMoreProgrammesQueries?.data?.data ?? ({} as ReadMoreProgramme);
  const courseOverviewData =
    readMoreCourseOverviewQueries?.data?.data ?? ({} as CourseOverview);
  const careerProspectData =
    readMoreCareerProspectQueries?.data?.data ?? ({} as CareerProspect);
  const careerProspectItemData = careerProspectItemQueries?.data?.data ?? [];
  const scholarshipData = scholarshipQueries?.data?.data ?? ({} as Scholarship);
  const testimonialData = testimonialQueries?.data?.data ?? ({} as Testimonial);
  const accreditationData =
    accreditationQueries?.data?.data ?? ({} as Accreditation);
  const tuitionData = tuitionQueries?.data?.data ?? ({} as Tuition);
  const applicationFeeData =
    applicationFeeQueries?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const curriculumData =
    curriculumQueries?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const admissionReqData =
    admissionReqQueries?.data?.data ?? ({} as AdmissionReq);
  const admissionReqDetailsData =
    admissionReqDetailsQueries?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const tuitionYearData =
    tuitionYearQueries?.data?.data?.filter((item) => item?.isActive === true) ??
    [];

  const isLoadingReadMoreProgram = readMoreProgrammesQueries?.isLoading;
  const isReadMoreProgramError = readMoreProgrammesQueries?.isError;
  const readMoreProgramError = readMoreProgrammesQueries?.error;

  const currentProspectId = careerProspectData?.id;
  const currentAdmissionReqId = admissionReqData?.id;
  const currentTuitionId = tuitionData?.id;

  const filteredFullTimeFees = applicationFeeData?.filter(
    (item) => item?.modeOfStudy === "Full-time"
  );
  const filteredPartTimeFees = applicationFeeData?.filter(
    (item) => item?.modeOfStudy === "Part-time"
  );

  useEffect(() => {
    setProspectId(String(currentProspectId));
    setAdmissionReqId(String(currentAdmissionReqId));
    setTuitionId(String(currentTuitionId));
  }, [currentProspectId, currentAdmissionReqId, currentTuitionId]);

  const handleNavigateToRegister = () => {
    navigate(`/register/${programTypeId}/${id}`);
  };

  if (isLoadingReadMoreProgram)
    return (
      <div className={styles.loader}>
        <Spin size="large" />
      </div>
    );
  if (isReadMoreProgramError) return <div>{readMoreProgramError?.message}</div>;

  return (
    <div className={styles.container}>
      <section className={styles.hero}>
        <div id="duration">
          <h1>{programmeData?.programName}</h1>
          <div
            dangerouslySetInnerHTML={sanitizeHtml(programmeData?.description)}
          />
          <br />

          <div className="small-gap">
            <div className={styles.details}>
              <Clock />
              <span>{programmeData?.duration}/Onsite</span>
            </div>
            <div className={styles.details}>
              <Calendar />
              <span>Intakes: {programmeData?.sessionIntake}</span>
            </div>
          </div>

          <br />
          <button onClick={handleNavigateToRegister}>Apply</button>
        </div>

        <div className={styles.toc}>
          <a href="#overview">Overview</a>
          <a href="#requirements">Requirements</a>
          <a href="#duration">Duration</a>
          <a href="#fee">Fee</a>
          <a href="#prospects">Career Prospects</a>
          <a href="#tuition">Tuition</a>
          <a href="#curriculum">Curriculum</a>
          <a href="#scholarship">Scholarship Opportunities</a>
          <a href="#testimonials">Student Testimonials</a>
          <a href="#accreditation">Accreditation</a>
        </div>
      </section>

      <section id="overview" className={styles.overview}>
        <h2>Course Overview</h2>
        <div
          dangerouslySetInnerHTML={sanitizeHtml(
            courseOverviewData?.description
          )}
        />
      </section>

      <br />
      <hr />

      <section id="requirements" className={styles.requirements}>
        <h2>Admission Requirements</h2>
        <div
          dangerouslySetInnerHTML={sanitizeHtml(admissionReqData?.description)}
        />

        <br />

        <div className={styles.tabs}>
          {admissionReqDetailsData?.map((tab, index) => (
            <div className={styles.tabWrapper} key={tab?.id}>
              <button
                onClick={() => setActive(index + 1)}
                className={classNames(
                  styles.tab,
                  active === index + 1 ? "active" : ""
                )}
              >
                {tab?.programTypeName}
              </button>
              {active === index + 1 ? (
                <div className={styles.active}></div>
              ) : null}
            </div>
          ))}
        </div>

        <br />
        <hr />
        <br />

        <div
          dangerouslySetInnerHTML={sanitizeHtml(
            admissionReqDetailsData[active - 1]?.description
          )}
        />
      </section>

      <hr />

      <section id="prospects" className={styles.prospects}>
        <h2>Career Prospects</h2>
        <div
          dangerouslySetInnerHTML={sanitizeHtml(
            careerProspectData?.description
          )}
        />

        <br />

        <div className={styles.grid}>
          {careerProspectItemData?.map((prospect) => (
            <div key={prospect?.id} className={styles.card}>
              <h2>{prospect?.title}</h2>
              <div
                dangerouslySetInnerHTML={sanitizeHtml(prospect?.description)}
              />
            </div>
          ))}
        </div>
      </section>

      <br />
      <hr />

      <section id="fee" className={styles.fee}>
        <h2>Application Fee</h2>
        <br />
        <div className={styles.cardWrapper}>
          {filteredFullTimeFees?.map((item) => (
            <div className={classNames(styles.card, styles.textStart)}>
              <p>
                <b>{item?.programTypeName}</b>
              </p>
              <p>
                <b>Full-time:</b> {item?.amount}
              </p>
            </div>
          ))}
        </div>

        <div className={styles.cardWrapper}>
          {filteredPartTimeFees?.map((item) => (
            <div className={classNames(styles.card, styles.textStart)}>
              <p>
                <b>{item?.programTypeName}</b>
              </p>
              <p>
                <b>Part-time:</b> {item?.amount}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section id="tuition" className={styles.tuition}>
        <h2>Tuition</h2>
        <div dangerouslySetInnerHTML={sanitizeHtml(tuitionData?.description)} />

        <br />

        {/* <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              {tuitionYearData?.map((item) => (
                <th>{item?.levelName}</th>
              ))}
            </thead>

            <tbody>
              <tr>
                {tuitionYearData?.map((item) => (
                  <td>
                    <div
                      dangerouslySetInnerHTML={sanitizeHtml(
                        item?.feeDescription
                      )}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div> */}

        <div className={styles.tabs}>
          {tuitionYearData?.map((tab, index) => (
            <div className={styles.tabWrapper} key={index}>
              <button
                onClick={() => setActiveYearTab(index + 1)}
                className={classNames(
                  styles.tab,
                  activeYearTab === index + 1 ? "active" : ""
                )}
              >
                {tab?.levelName}
              </button>

              {activeYearTab === index + 1 ? (
                <div className={styles.active}></div>
              ) : null}
            </div>
          ))}
        </div>

        <br />
        <hr />
        <br />

        <div
          dangerouslySetInnerHTML={sanitizeHtml(
            tuitionYearData[activeYearTab - 1]?.feeDescription
          )}
        />
      </section>

      <section id="curriculum" className={styles.curriculum}>
        <h2>Curriculum</h2>
        <br />
        <div className={styles.tabs}>
          {curriculumData?.map((tab, index) => (
            <div className={styles.tabWrapper} key={index}>
              <button
                onClick={() => setActiveCurriculumTab(index + 1)}
                className={classNames(
                  styles.tab,
                  activeCurriculumTab === index + 1 ? "active" : ""
                )}
              >
                {tab?.levelName}
              </button>
              
              {activeCurriculumTab === index + 1 ? (
                <div className={styles.active}></div>
              ) : null}
            </div>
          ))}
        </div>
        <br />
        <hr />
        <br />

        <div
          dangerouslySetInnerHTML={sanitizeHtml(
            curriculumData[activeCurriculumTab - 1]?.description
          )}
        />
      </section>

      <section id="scholarship" className={styles.scholarship}>
        <h2>Scholarship Opportunities</h2>
        <div
          dangerouslySetInnerHTML={sanitizeHtml(scholarshipData?.description)}
        />
      </section>

      <section id="testimonials" className={styles.testimonials}>
        <h2>Student Testimonials</h2>
        <p>
          <img src={testimonialData?.imageUrl} alt="" />
        </p>
        <div
          dangerouslySetInnerHTML={sanitizeHtml(testimonialData?.description)}
        />
      </section>

      <br />
      <hr />

      <section id="accreditation" className={styles.accreditation}>
        <h2>Accreditation</h2>
        <div
          dangerouslySetInnerHTML={sanitizeHtml(accreditationData?.description)}
        />
      </section>

      <section className={styles.apply}>
        <h2>Are you ready to take the next step toward your future career?</h2>
        <button onClick={handleNavigateToRegister}>Apply</button>
      </section>
    </div>
  );
};

export default CourseDetails;
